// Turning a suggest response into the rows the panel draws.
//
// Two jobs live here, both pure so they can be tested without a network or a
// DOM: deciding whether a query is even worth suggesting on, and working out
// which part of each row the user's query actually matched so it can be
// highlighted.

import { SuggestMatch, SuggestResult } from 'core/interfaces/dictionary-interfaces'
import { DictionaryRoute, englishRoute, thaiRoute } from './dictionary-routes'
import { isThaiText } from './generic-utils/strings'
import { preprocessQuery } from './preprocessQuery'

// What the dictionary worker will do with this query, decided before spending a
// request on it:
//   empty    — nothing typed; the panel stays shut
//   sentence — too long or too many words to be a headword; the panel stays
//              shut too. Autocomplete has nothing useful to say about a phrase,
//              and ⏎ already sends it to the translator, so a row saying so was
//              just something in the way.
//   lookup   — worth asking about
//
// There is no 'short': the worker suggests from the first character in both
// languages (see the note on prefix floors in handlers/suggest.ts), so anything
// typed at all is worth asking about.
export type SuggestQueryKind = 'empty' | 'sentence' | 'lookup'

// Past this, Thai stops being a plausible single headword. Thai is written
// without spaces, so length is the only signal available — word count can't be.
const MAX_THAI_HEADWORD_LENGTH = 14

// Beyond this many words it's a phrase to translate, not an entry to look up.
// Matches dictionaryRouteFor's own 4-word ceiling, so the panel and the submit
// path agree about what counts as a sentence.
const MAX_LOOKUP_WORDS = 4

export const classifySuggestQuery = (rawQuery: string): SuggestQueryKind => {
   const query = preprocessQuery(rawQuery)
   if (!query) return 'empty'

   // Interior newlines are always a translation job — preprocessQuery keeps them
   // for exactly this reason.
   if (query.includes('\n')) return 'sentence'

   const thai = isThaiText(query)
   if (thai && query.length >= MAX_THAI_HEADWORD_LENGTH) return 'sentence'
   if (!thai && query.split(/\s+/).length > MAX_LOOKUP_WORDS) return 'sentence'

   return 'lookup'
}

/** A string cut into the part before the match, the match, and the part after. */
export type HighlightParts = {
   pre: string
   mid: string
   post: string
}

const NO_MATCH = (text: string): HighlightParts => ({ pre: text, mid: '', post: '' })

// Characters that carry no matching signal in a romanisation or an English
// headword: syllable hyphens, spaces, apostrophes, and the interpunct that
// separates meanings.
const NOISE = /[-\s'’·.]/g

/**
 * Reduce one character to its matching form: strip its accent and lowercase it.
 * This is what lets a typed `am-naat` find the rendered `am-nâat` — the user is
 * spelling a sound, not copying a diacritic.
 */
const foldChar = (char: string): string =>
   char
      .normalize('NFD')
      .toLowerCase()
      .replace(/[̀-ͯ]/g, '')
      .replace(NOISE, '')

/**
 * Fold a whole string, keeping a map from each folded character back to the
 * index it came from. The map is what makes the highlight land on the original
 * text: fold `am-nâat` and the hit is found at folded offset 2, which maps back
 * to original offset 3 — past the hyphen the fold removed.
 */
const fold = (text: string): { folded: string; indexes: number[] } => {
   let folded = ''
   const indexes: number[] = []
   for (let i = 0; i < text.length; i++) {
      const chars = foldChar(text[i])
      for (const char of chars) {
         folded += char
         indexes.push(i)
      }
   }
   return { folded, indexes }
}

/**
 * Where `query` lands inside `text`, as `{pre, mid, post}` slices of the
 * original. Thai script matches literally — its characters carry meaning that
 * folding would destroy, and there are no accents or hyphens to see past.
 * Everything else matches folded, so spelling a sound loosely still highlights.
 */
export const highlight = (text: string, query: string, literal: boolean): HighlightParts => {
   if (!text || !query) return NO_MATCH(text)

   let start: number
   let end: number

   if (literal) {
      start = text.indexOf(query)
      if (start < 0) return NO_MATCH(text)
      end = start + query.length
   } else {
      const target = fold(text)
      const needle = fold(query).folded
      if (!needle) return NO_MATCH(text)

      const at = target.folded.indexOf(needle)
      if (at < 0) return NO_MATCH(text)

      start = target.indexes[at]
      end = target.indexes[at + needle.length - 1] + 1
   }

   return {
      pre: text.slice(0, start),
      mid: text.slice(start, end),
      post: text.slice(end),
   }
}

/** One row of the panel, with everything it renders already resolved. */
export type SuggestItem = {
   word: string
   route: DictionaryRoute
   // Rendered in the Thai face when this is a Thai headword, the Latin one when
   // it's English.
   isThaiHeadword: boolean
   head: HighlightParts
   // Already run through the user's transliteration scheme by the caller — the
   // highlight has to land on what is shown, not on the raw phonetic.
   translit: HighlightParts
   meaning: HighlightParts
   commonnessScore: number
}

export type SuggestGroup = {
   label: string
   items: SuggestItem[]
}

const THAI_LABEL = 'Thai words'
const ENGLISH_LABEL = 'English words'
const SOUND_LABEL = 'Thai words · matched by sound'

const toItems = (
   matches: SuggestMatch[],
   query: string,
   isThaiHeadword: boolean,
   renderTranslit: (t2e: string) => string
): SuggestItem[] => {
   // Thai script is matched literally against a Thai headword. Everything else —
   // an English headword, a romanisation, an English meaning — is folded.
   const literalHead = isThaiHeadword && isThaiText(query)

   return matches.map((match) => {
      const translit = match.t2e ? renderTranslit(match.t2e) : ''
      return {
         word: match.word,
         route: isThaiHeadword ? thaiRoute(match.word) : englishRoute(match.word),
         isThaiHeadword,
         head: highlight(match.word, query, literalHead),
         translit: highlight(translit, query, false),
         // An English entry's meaning is Thai, and vice versa, so the meaning
         // line is matched the opposite way round from the headword.
         meaning: highlight(match.meaning, query, !literalHead && isThaiText(query)),
         commonnessScore: match.commonnessScore,
      }
   })
}

/**
 * The panel's groups, in the order they are shown. The worker has already
 * decided which lists a query populates — Thai script only fills `thaiMatches`,
 * Latin fills `engMatches` and/or `translitMatches` — so this maps rather than
 * re-deciding, and empty lists simply don't produce a group.
 */
export const toSuggestGroups = (
   result: SuggestResult,
   query: string,
   renderTranslit: (t2e: string) => string
): SuggestGroup[] => {
   const groups: SuggestGroup[] = []

   const add = (label: string, matches: SuggestMatch[], isThaiHeadword: boolean) => {
      if (matches.length === 0) return
      const items = toItems(matches, query, isThaiHeadword, renderTranslit)
      groups.push({ label, items })
   }

   add(THAI_LABEL, result.thaiMatches, true)
   add(ENGLISH_LABEL, result.engMatches, false)
   // Last: a sound-alike is a guess about what was meant, so it sits below the
   // groups that matched what was actually typed.
   add(SOUND_LABEL, result.translitMatches, true)

   return groups
}

/** Every item across every group, in display order — what ↑↓ walks. */
export const flattenGroups = (groups: SuggestGroup[]): SuggestItem[] =>
   groups.flatMap((group) => group.items)
